import React, { createContext, useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import MultiBackend, { TouchTransition, MouseTransition, Preview } from 'react-dnd-multi-backend';

import useEditorContext from './editorContext';

const DragDropContext = createContext();

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true, delayTouchStart: 100 },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

export const DragDropContextProvider = props => (
  <DndProvider backend={MultiBackend} options={HTML5toTouch} context={window}>
    <DragDropContext.Provider value={{ id: props.id }}>{props.children}</DragDropContext.Provider>

    <Preview>
      <Preview.Context.Consumer>
        {({ item, style }) => (
          <div
            style={{
              ...style,
              transition: 'none',
              opacity: 0.8,
              background: `url(${item.src}) no-repeat center / cover`,
              width: item.size.width,
              height: item.size.height,
              zIndex: 10000,
            }}
          />
        )}
      </Preview.Context.Consumer>
    </Preview>
  </DndProvider>
);

DragDropContextProvider.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node,
};

export const Draggable = forwardRef((props, ref) => {
  const [{ opacity }, drag] = useDrag({
    item: {
      type: 'PHOTO',
      page: props.pageIndex,
      placeholder: props.placeholderId,
      photo: props.photoKey,
      src: props.src,
      size: props.size,
    },
    options: {
      dropEffect: 'move',
    },
    canDrag: props.canDrag,
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
  });

  drag(ref);

  return { ...props.children, ref };
});

Draggable.propTypes = {
  pageIndex: PropTypes.number,
  placeholderId: PropTypes.string,
  photoKey: PropTypes.string,
  src: PropTypes.string,
  canDrag: PropTypes.bool,
  children: PropTypes.node,
};

export const Droppable = forwardRef((props, ref) => {
  const dnd = useContext(DragDropContext);
  const editorContext = useEditorContext();

  const [{ isActive }, drop] = useDrop({
    accept: 'PHOTO',
    collect: monitor => ({
      isActive: monitor.canDrop() && monitor.isOver(),
    }),
    drop: item => {
      editorContext.swapPhotos(
        dnd.id,
        item.page,
        item.placeholder,
        item.photo,
        props.pageIndex,
        props.placeholderId,
        props.photoKey,
      );
    },
  });

  drop(ref);

  return { ...props.children, ref };
});

Droppable.propTypes = {
  pageIndex: PropTypes.number,
  placeholderId: PropTypes.string,
  photoKey: PropTypes.string,
  children: PropTypes.node,
};
